import { downloadDocument, IMail, IMailroom } from '@commenda/api';
import { Button, DocViewerComponent, useToggle } from '@commenda/kakashi';
import { DownloadDocumentType } from '@commenda/types';
import { downloadDocumentLocally } from '@commenda/utils/documents/download-document';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { generateGmailLink } from '@commenda/utils';
import { Download } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { GetHelpModal } from './Modals/GetHelpModal';

type MailItemPreviewProps = {
  mail: IMail & { mailroom: IMailroom };
};

const MailItemPreview = ({ mail }: MailItemPreviewProps) => {
  const [documentLink, setDocumentLink] = useState<string | null>(null);
  const getHelpModalVisibility = useToggle(false);

  useEffect(() => {
    if (mail.files?.[0]?.id) {
      const asyncFetchDocumentLink = async () => {
        const link = await getSignedUrlByFileId(mail.files[0].id, null);
        if (link) setDocumentLink(link);
      };
      asyncFetchDocumentLink();
    }
  }, [mail.files]);

  const DocViewerComp = useMemo(() => {
    if (!documentLink) return null;
    return (
      <div className="h-full w-full overflow-y-auto">
        <DocViewerComponent uri={documentLink} />
      </div>
    );
  }, [documentLink]);

  async function downloadDocumentData(fileId: number) {
    const responseBlob = await downloadDocument({
      fileId,
      fileType: DownloadDocumentType.PDF,
    });

    downloadDocumentLocally({
      fileName: mail.files?.[0]?.fileName,
      fileType: DownloadDocumentType.PDF,
      blob: responseBlob,
    });
  }

  return (
    <div className="flex-1 flex flex-col">
      <div className="p-4 border-b flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">{mail.title}</h2>
        </div>

        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={getHelpModalVisibility.toggle}
          >
            Request support
          </Button>
          <Button
            size="sm"
            onClick={() => downloadDocumentData(mail.files?.[0]?.id)}
          >
            <Download className="size-4" />
            Download
          </Button>
        </div>
      </div>
      <div className="flex-1 p-4 overflow-auto">{DocViewerComp}</div>
      <GetHelpModal
        isOpen={getHelpModalVisibility.visible}
        onClose={getHelpModalVisibility.toggle}
        mail={mail}
      />
    </div>
  );
};

export { MailItemPreview };
